import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Card, Grid, Table, TableHead, TableRow, TableCell, TableBody,
  Button, IconButton, Typography, TextField, MenuItem, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Tooltip,
  Chip, LinearProgress, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Edit, Search, Email, Upload, Delete, CheckCircle, Warning, LockOpen, Lock, ArrowUpward, ArrowDownward, PictureAsPdf, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import StatusChip from '../common/StatusChip';
import { MobileCard, MobileRow } from '../common/MobileCard';
import { useAuth } from '../../contexts/AuthContext';

export default function RemittanceList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canEdit = isAdmin || isManager;
  const canDelete = isAdmin;

  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [remStatusFilter, setRemStatusFilter] = useState('all');
  const [dateSort, setDateSort] = useState('desc'); // 'asc' | 'desc'
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: '', remittance_status: 'open', bank_reference: '', kotak_reference: '', completion_date: '' });
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [kotakPdfResult, setKotakPdfResult] = useState(null);
  const [kotakPdfLoading, setKotakPdfLoading] = useState(false);
  const [kotakCreateForm, setKotakCreateForm] = useState(null);
  const [kotakCreateLoading, setKotakCreateLoading] = useState(false);
  const [viewDialog, setViewDialog] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const kotakPdfRef = useRef();
  const navigate = useNavigate();

  const load = () => api.get('/remittances').then(r => setList(r.data.data));
  useEffect(() => {
    load();
    api.get('/beneficiaries').then(r => setBeneficiaries(r.data.data));
  }, []);
  useEffect(() => {
    let d = list;
    if (search) {
      const q = search.toLowerCase();
      d = d.filter(r =>
        r.remittance_number?.toLowerCase().includes(q) ||
        r.beneficiary_name?.toLowerCase().includes(q) ||
        r.bank_reference?.toLowerCase().includes(q) ||
        r.kotak_reference?.toLowerCase().includes(q) ||
        String(r.amount || '').includes(q)
      );
    }
    if (statusFilter !== 'all') d = d.filter(r => r.status === statusFilter);
    if (remStatusFilter !== 'all') d = d.filter(r => (r.remittance_status || 'open') === remStatusFilter);
    d = [...d].sort((a, b) => {
      const diff = new Date(a.remittance_date) - new Date(b.remittance_date);
      return dateSort === 'asc' ? diff : -diff;
    });
    setFiltered(d);
  }, [search, statusFilter, remStatusFilter, dateSort, list]);

  const openStatusDialog = (r) => {
    setStatusDialog(r);
    setStatusForm({ status: r.status, remittance_status: r.remittance_status || 'open', bank_reference: r.bank_reference || '', kotak_reference: r.kotak_reference || '', completion_date: r.completion_date || '' });
  };

  const handleStatusUpdate = async () => {
    try {
      await api.put(`/remittances/${statusDialog.id}/status`, statusForm);
      setStatusDialog(null); load();
    } catch (err) { setError(err.response?.data?.message || 'Error'); }
  };

  const toggleRemittanceStatus = async (r) => {
    const next = (r.remittance_status || 'open') === 'open' ? 'closed' : 'open';
    try {
      await api.put(`/remittances/${r.id}/status`, { status: r.status, remittance_status: next });
      load();
    } catch (err) { setError('Failed to update status'); }
  };

  const openEmailDialog = (r) => {
    setEmailDialog(r);
    setEmailForm({
      to: 'forex@kotak.com',
      subject: `FX Remittance Reference - ${r.remittance_number}`,
      body: `Dear Team,\n\nPlease process the following outward remittance:\n\nRef: ${r.remittance_number}\nBeneficiary: ${r.beneficiary_name}\nAmount: ${r.currency} ${parseFloat(r.amount).toLocaleString()}\nKotak Ref: ${r.kotak_reference || 'N/A'}\n\nRegards,\nIdentium Tech Solutions Pvt Ltd`
    });
  };

  const handleSendEmail = async () => {
    try { await api.post(`/remittances/${emailDialog.id}/send-email`, emailForm); setEmailDialog(null); load(); }
    catch (err) { setError(err.response?.data?.message || 'Email failed'); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/remittances/${deleteDialog.id}`);
      setDeleteDialog(null); load();
    } catch (err) { setError(err.response?.data?.message || 'Delete failed'); setDeleteDialog(null); }
  };

  const handleKotakPdf = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setKotakPdfLoading(true); setKotakPdfResult(null); setKotakCreateForm(null); setError('');
    try {
      const fd = new FormData();
      fd.append('pdf_file', file);
      const res = await api.post('/remittances/import-kotak-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setKotakPdfResult(res.data);
      if (res.data.action === 'updated') {
        load();
      } else if (res.data.action === 'not_found') {
        const p = res.data.parsed;
        // Multi-strategy beneficiary matching
        const matchBeneficiary = (pdfName) => {
          if (!pdfName) return null;
          const norm = s => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
          const pdfN = norm(pdfName);
          // 1. Exact match
          let m = beneficiaries.find(b => norm(b.name) === pdfN);
          if (m) return m;
          // 2. One name fully contains the other
          m = beneficiaries.find(b => {
            const dbN = norm(b.name);
            return dbN.includes(pdfN) || pdfN.includes(dbN);
          });
          if (m) return m;
          // 3. Significant-word overlap (ignore city names, generic suffixes)
          const stopWords = new Set(['co', 'ltd', 'limited', 'inc', 'corp', 'company', 'the', 'of', 'and', 'for', 'group', 'international', 'shenzhen', 'guangzhou', 'guangdong', 'dongguan', 'huizhou', 'zhongshan', 'yueqing', 'hongkong', 'beijing', 'shanghai']);
          const sig = s => norm(s).split(' ').filter(w => w.length >= 3 && !stopWords.has(w));
          const pdfWords = new Set(sig(pdfName));
          if (pdfWords.size === 0) return null;
          let best = null, bestScore = 0;
          for (const b of beneficiaries) {
            const dbWords = sig(b.name);
            const overlap = dbWords.filter(w => pdfWords.has(w)).length;
            if (overlap > 0) {
              const score = overlap / Math.max(pdfWords.size, dbWords.length);
              if (score > bestScore) { bestScore = score; best = b; }
            }
          }
          return bestScore >= 0.4 ? best : null;
        };
        const bMatch = matchBeneficiary(p.beneficiary_name);
        setKotakCreateForm({
          remittance_number: p.remittance_number || '',
          kotak_reference: p.kotak_reference || '',
          remittance_date: p.completion_date || new Date().toISOString().split('T')[0],
          completion_date: p.completion_date || '',
          beneficiary_id: bMatch?.id || '',
          currency: 'USD',
          amount: p.amount || '',
          exchange_rate: p.exchange_rate || '',
          inr_amount: p.inr_amount || '',
          bank_charges: p.bank_charges || '',
          bank_charges_gst: p.bank_charges_gst || '',
          bank_charges_fcc_gst: p.bank_charges_fcc_gst || '',
          purpose_code: 'P0101',
          purpose_description: 'Advance Import Remittance(AIR)',
          notes: ''
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to parse PDF');
    } finally { setKotakPdfLoading(false); }
  };

  const handleKotakCreate = async () => {
    if (!kotakCreateForm?.beneficiary_id) { setError('Please select a beneficiary'); return; }
    setKotakCreateLoading(true);
    try {
      await api.post('/remittances', {
        ...kotakCreateForm,
        status: 'completed',
        kotak_advice_path: kotakPdfResult?.kotak_advice_path
      });
      setKotakPdfResult(null); setKotakCreateForm(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create remittance');
    } finally { setKotakCreateLoading(false); }
  };

  // Excel import — Kotak format
  // Expected columns: Portal Ref #, Remittance Type, Master Reference, Beneficiary Name, CCY, Amount
  const handleExcelFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        // Find header row (look for "Portal Ref" or "Ref #" in any row)
        let headerIdx = 0;
        for (let i = 0; i < Math.min(raw.length, 10); i++) {
          const rowStr = raw[i].join(' ').toLowerCase();
          if (rowStr.includes('portal') || rowStr.includes('remittance') || rowStr.includes('beneficiary')) {
            headerIdx = i; break;
          }
        }
        const headers = raw[headerIdx].map(h => String(h).toLowerCase().trim());
        const colIdx = (keywords) => {
          for (const kw of keywords) {
            const idx = headers.findIndex(h => h.includes(kw));
            if (idx >= 0) return idx;
          }
          return -1;
        };
        const portalRefCol   = colIdx(['portal ref', 'ref #', 'ft', 'portal']);
        const typeCol        = colIdx(['type', 'remittance type']);
        const masterRefCol   = colIdx(['master ref', 'master reference', 'master']);
        const beneficiaryCol = colIdx(['beneficiary', 'name']);
        const ccyCol         = colIdx(['ccy', 'currency']);
        const amountCol      = colIdx(['amount']);
        const dateCol        = colIdx(['date', 'value date']);

        const rows = [];
        for (let i = headerIdx + 1; i < raw.length; i++) {
          const row = raw[i];
          if (!row.some(c => c !== '')) continue; // skip empty rows
          const portal_ref = portalRefCol >= 0 ? String(row[portalRefCol] || '').trim() : '';
          const amount_raw = amountCol >= 0 ? row[amountCol] : '';
          const amount = parseFloat(String(amount_raw).replace(/,/g, '')) || 0;
          if (!portal_ref && amount === 0) continue;

          // Try to match beneficiary
          const bName = beneficiaryCol >= 0 ? String(row[beneficiaryCol] || '').trim() : '';
          const matched = beneficiaries.find(b =>
            bName && b.name.toLowerCase().includes(bName.toLowerCase().substring(0, 6))
          );

          rows.push({
            remittance_number: portal_ref,
            remittance_type: typeCol >= 0 ? String(row[typeCol] || '').trim() : '',
            kotak_reference: masterRefCol >= 0 ? String(row[masterRefCol] || '').trim() : '',
            beneficiary_name_raw: bName,
            beneficiary_id: matched?.id || '',
            currency: ccyCol >= 0 ? String(row[ccyCol] || 'USD').trim() : 'USD',
            amount,
            remittance_date: dateCol >= 0 ? String(row[dateCol] || '').trim() : new Date().toISOString().split('T')[0],
            status: 'completed',
            _matched: !!matched,
          });
        }
        setImportRows(rows);
        setImportResult(null);
        setImportDialog(true);
      } catch (err) {
        setError('Failed to parse Excel file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    setImportLoading(true);
    let imported = 0, skipped = 0;
    for (const row of importRows) {
      if (!row.remittance_number || !row.amount) { skipped++; continue; }
      try {
        await api.post('/remittances', {
          remittance_number: row.remittance_number,
          kotak_reference: row.kotak_reference,
          beneficiary_id: row.beneficiary_id || null,
          currency: row.currency || 'USD',
          amount: row.amount,
          remittance_date: row.remittance_date || new Date().toISOString().split('T')[0],
          status: 'completed',
          purpose_of_remittance: row.remittance_type || 'Advance Import Remittance(AIR)',
          notes: `Imported from Kotak portal. Beneficiary: ${row.beneficiary_name_raw}`
        });
        imported++;
      } catch (err) {
        skipped++; // likely duplicate
      }
    }
    setImportLoading(false);
    setImportResult({ imported, skipped });
    load();
  };

  const totalAmount = filtered.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  return (
    <Box>
      <PageHeader title="Outward Remittances" subtitle="Track all forex payments to suppliers"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleExcelFile} />
            <input ref={kotakPdfRef} type="file" accept=".pdf" hidden onChange={handleKotakPdf} />
            <Button variant="outlined" startIcon={<Upload />} size={isMobile ? 'small' : 'medium'}
              onClick={() => fileInputRef.current?.click()}>
              {isMobile ? 'Excel' : 'Import Excel'}
            </Button>
            <Button variant="outlined" color="secondary" startIcon={<PictureAsPdf />} size={isMobile ? 'small' : 'medium'}
              disabled={kotakPdfLoading}
              onClick={() => kotakPdfRef.current?.click()}>
              {kotakPdfLoading ? 'Reading…' : (isMobile ? 'Kotak PDF' : 'Import Kotak PDF')}
            </Button>
            <Button variant="contained" startIcon={<Add />} size={isMobile ? 'small' : 'medium'}
              onClick={() => navigate('/remittances/new')}>
              {isMobile ? 'New' : 'New Remittance'}
            </Button>
          </Box>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 160 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 130 }} label="Bank Status">
          <MenuItem value="all">All</MenuItem>
          {['draft','submitted','processing','completed','failed','cancelled'].map(s => (
            <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" select value={remStatusFilter} onChange={e => setRemStatusFilter(e.target.value)}
          sx={{ minWidth: 120 }} label="Open / Closed">
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </TextField>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {filtered.length} records · Total: <strong>${totalAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}</strong>
      </Typography>

      {/* Mobile cards */}
      {isMobile ? (
        <Box>
          {filtered.length === 0
            ? <Card><Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No remittances found</Box></Card>
            : filtered.map(r => (
              <MobileCard key={r.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 11, color: 'primary.main' }}>
                      {r.remittance_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{r.remittance_date}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <StatusChip status={r.status} />
                    <IconButton size="small" onClick={() => navigate(`/remittances/${r.id}`)} sx={{ color: 'primary.main' }}><Visibility fontSize="small" /></IconButton>
                    {canEdit && <IconButton size="small" onClick={() => openStatusDialog(r)}><Edit fontSize="small" /></IconButton>}
                    <IconButton size="small" onClick={() => openEmailDialog(r)} sx={{ color: 'info.main' }}><Email fontSize="small" /></IconButton>
                    {canDelete && <IconButton size="small" onClick={() => setDeleteDialog(r)} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton>}
                  </Box>
                </Box>
                <MobileRow label="Beneficiary" value={r.beneficiary_name} />
                <MobileRow label="Amount"      value={`${r.currency} ${parseFloat(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} bold />
                {r.inr_amount && <MobileRow label="INR" value={`₹${parseFloat(r.inr_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />}
                {r.kotak_reference && <MobileRow label="Kotak Ref" value={r.kotak_reference} mono />}
                {r.bank_reference  && <MobileRow label="FT Number"  value={r.bank_reference} mono />}
              </MobileCard>
            ))
          }
        </Box>
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kotak Ref</TableCell>
                <TableCell onClick={() => setDateSort(s => s === 'asc' ? 'desc' : 'asc')}
                  sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  Date {dateSort === 'asc' ? <ArrowUpward sx={{ fontSize: 13, verticalAlign: 'middle' }} /> : <ArrowDownward sx={{ fontSize: 13, verticalAlign: 'middle' }} />}
                </TableCell>
                <TableCell>Beneficiary</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Bank Status</TableCell>
                <TableCell>Open/Closed</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No remittances found</TableCell></TableRow>
              ) : filtered.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontSize: 11 }}>
                    <div style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1976d2' }}>{r.kotak_reference || '—'}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#999' }}>{r.remittance_number}</div>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.remittance_date}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={500}>{r.beneficiary_name}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600} color="primary.main" variant="body2">{r.currency} {parseFloat(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                    {r.inr_amount && <Typography variant="caption" color="text.secondary" display="block">₹{parseFloat(r.inr_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>}
                    {r.exchange_rate && <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10 }} display="block">@ {parseFloat(r.exchange_rate).toFixed(4)}</Typography>}
                    {r.bank_charges && <Typography variant="caption" sx={{ color: '#f57c00', fontSize: 10 }} display="block">Chg: ₹{parseFloat(r.bank_charges).toFixed(0)} + GST ₹{(parseFloat(r.bank_charges_gst || 0) + parseFloat(r.bank_charges_fcc_gst || 0)).toFixed(0)}</Typography>}
                  </TableCell>
                  <TableCell><StatusChip status={r.status} /></TableCell>
                  <TableCell>
                    <Tooltip title={`Click to mark as ${(r.remittance_status || 'open') === 'open' ? 'Closed' : 'Open'}`}>
                      <Chip
                        icon={(r.remittance_status || 'open') === 'open' ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                        label={(r.remittance_status || 'open') === 'open' ? 'Open' : 'Closed'}
                        size="small"
                        color={(r.remittance_status || 'open') === 'open' ? 'success' : 'default'}
                        variant={(r.remittance_status || 'open') === 'open' ? 'filled' : 'outlined'}
                        onClick={() => canEdit && toggleRemittanceStatus(r)}
                        sx={{ cursor: canEdit ? 'pointer' : 'default' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details"><IconButton size="small" onClick={() => navigate(`/remittances/${r.id}`)} sx={{ color: 'primary.main' }}><Visibility fontSize="small" /></IconButton></Tooltip>
                    {canEdit && <Tooltip title="Update Status"><IconButton size="small" onClick={() => openStatusDialog(r)}><Edit fontSize="small" /></IconButton></Tooltip>}
                    <Tooltip title="Send to Bank"><IconButton size="small" onClick={() => openEmailDialog(r)} sx={{ color: 'info.main' }}><Email fontSize="small" /></IconButton></Tooltip>
                    {canDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog(r)} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Status Dialog */}
      <Dialog open={Boolean(statusDialog)} onClose={(_, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setStatusDialog(null); }} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{statusDialog?.remittance_number}</Typography>
          <TextField label="Bank Status" select fullWidth sx={{ mb: 2 }} value={statusForm.status} onChange={e => setStatusForm({ ...statusForm, status: e.target.value })}>
            {['draft','submitted','processing','completed','failed','cancelled'].map(s => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField label="Open / Closed" select fullWidth sx={{ mb: 2 }} value={statusForm.remittance_status} onChange={e => setStatusForm({ ...statusForm, remittance_status: e.target.value })}>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
          <TextField label="Kotak Master Ref" fullWidth sx={{ mb: 2 }} value={statusForm.kotak_reference} onChange={e => setStatusForm({ ...statusForm, kotak_reference: e.target.value })} placeholder="4635AIR261423425" />
          <TextField label="Bank FT Number"   fullWidth sx={{ mb: 2 }} value={statusForm.bank_reference}  onChange={e => setStatusForm({ ...statusForm, bank_reference: e.target.value })}  placeholder="FT26020006459389" />
          <TextField label="Completion Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={statusForm.completion_date} onChange={e => setStatusForm({ ...statusForm, completion_date: e.target.value })} />
          {(statusDialog?.exchange_rate || statusDialog?.bank_charges) && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, fontSize: 12 }}>
              {statusDialog?.exchange_rate && <div>Exchange Rate: <strong>₹{parseFloat(statusDialog.exchange_rate).toFixed(4)}</strong> / USD</div>}
              {statusDialog?.inr_amount && <div>INR Principal: <strong>₹{parseFloat(statusDialog.inr_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong></div>}
              {statusDialog?.bank_charges && <div>Bank Charges: <strong>₹{parseFloat(statusDialog.bank_charges).toFixed(2)}</strong></div>}
              {statusDialog?.bank_charges_gst && <div>GST on Charges: <strong>₹{parseFloat(statusDialog.bank_charges_gst).toFixed(2)}</strong></div>}
              {statusDialog?.bank_charges_fcc_gst && <div>FCC-GST on Charges: <strong>₹{parseFloat(statusDialog.bank_charges_fcc_gst).toFixed(2)}</strong></div>}
              {statusDialog?.bank_charges && <div style={{ borderTop: '1px solid #ddd', marginTop: 4, paddingTop: 4 }}>Total Debit: <strong>₹{(parseFloat(statusDialog.inr_amount || 0) + parseFloat(statusDialog.bank_charges || 0) + parseFloat(statusDialog.bank_charges_gst || 0) + parseFloat(statusDialog.bank_charges_fcc_gst || 0)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong></div>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStatusDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusUpdate}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={Boolean(emailDialog)} onClose={(_, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setEmailDialog(null); }} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Send Reference to Bank</DialogTitle>
        <DialogContent>
          <TextField label="To" fullWidth sx={{ mb: 2, mt: 1 }} value={emailForm.to} onChange={e => setEmailForm({ ...emailForm, to: e.target.value })} />
          <TextField label="Subject" fullWidth sx={{ mb: 2 }} value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} />
          <TextField label="Message" fullWidth multiline rows={8} value={emailForm.body} onChange={e => setEmailForm({ ...emailForm, body: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailDialog(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<Email />} onClick={handleSendEmail}>Send</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={Boolean(deleteDialog)} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Remittance</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteDialog?.remittance_number}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Excel Import Dialog */}
      <Dialog open={importDialog} onClose={(_, r) => { if (r === 'backdropClick' || r === 'escapeKeyDown') return; setImportDialog(false); setImportRows([]); setImportResult(null); }} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>Import Remittances from Excel (Kotak Format)</DialogTitle>
        <DialogContent>
          {importLoading && <LinearProgress sx={{ mb: 2 }} />}
          {importResult && (
            <Alert severity={importResult.skipped > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              Imported: <strong>{importResult.imported}</strong> records.
              {importResult.skipped > 0 && <> Skipped: <strong>{importResult.skipped}</strong> (duplicates or missing data).</>}
            </Alert>
          )}
          {importRows.length > 0 && !importResult && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {importRows.length} records found. Review before importing:
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Portal Ref #</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Master Reference</TableCell>
                      <TableCell>Beneficiary</TableCell>
                      <TableCell>CCY</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Match</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{row.remittance_number}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{row.remittance_type}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{row.kotak_reference}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{row.beneficiary_name_raw}</TableCell>
                        <TableCell>{row.currency}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{row.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{row.remittance_date}</TableCell>
                        <TableCell>
                          {row._matched
                            ? <Chip icon={<CheckCircle />} label="Matched" size="small" color="success" />
                            : <Chip icon={<Warning />} label="No match" size="small" color="warning" variant="outlined" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
          {importRows.length === 0 && !importResult && (
            <Typography color="text.secondary">No rows parsed from file.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setImportDialog(false); setImportRows([]); setImportResult(null); }}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {importRows.length > 0 && !importResult && (
            <Button variant="contained" startIcon={<Upload />} onClick={handleImport} disabled={importLoading}>
              Import {importRows.length} Records
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Kotak PDF Import Result / Create Form */}
      <Dialog
        open={Boolean(kotakPdfResult)}
        onClose={() => { setKotakPdfResult(null); setKotakCreateForm(null); }}
        maxWidth={kotakPdfResult?.action === 'not_found' ? 'md' : 'sm'}
        fullWidth
        fullScreen={isMobile && kotakPdfResult?.action === 'not_found'}
      >
        <DialogTitle>
          {kotakPdfResult?.action === 'updated' ? '✅ Remittance Updated' :
           kotakPdfResult?.action === 'not_found' ? 'Create Remittance from PDF' : 'Import Result'}
        </DialogTitle>
        <DialogContent>
          {kotakPdfResult?.action === 'updated' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Record for <strong>{kotakPdfResult.parsed?.kotak_reference}</strong> updated with exchange rate, INR amount and charges.
              </Alert>
              <Box sx={{ fontSize: 13, lineHeight: 2 }}>
                {[
                  ['Kotak Ref',        kotakPdfResult.parsed?.kotak_reference],
                  ['Exchange Rate',    kotakPdfResult.parsed?.exchange_rate ? `₹ ${kotakPdfResult.parsed.exchange_rate}` : null],
                  ['INR Principal',    kotakPdfResult.parsed?.inr_amount ? `₹ ${parseFloat(kotakPdfResult.parsed.inr_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : null],
                  ['Bank Charges',     kotakPdfResult.parsed?.bank_charges ? `₹ ${kotakPdfResult.parsed.bank_charges}` : null],
                  ['GST on Charges',   kotakPdfResult.parsed?.bank_charges_gst ? `₹ ${kotakPdfResult.parsed.bank_charges_gst}` : null],
                  ['FCC-GST',          kotakPdfResult.parsed?.bank_charges_fcc_gst ? `₹ ${kotakPdfResult.parsed.bank_charges_fcc_gst}` : null],
                  ['Completion Date',  kotakPdfResult.parsed?.completion_date],
                ].filter(([,v]) => v).map(([label, val]) => (
                  <Box key={label} sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130 }}>{label}</Typography>
                    <Typography variant="caption" fontWeight={600}>{val}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {kotakPdfResult?.action === 'not_found' && kotakCreateForm && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                No existing remittance found for Kotak Ref <strong>{kotakCreateForm.kotak_reference}</strong>. Review the pre-filled data below and create a new record.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="FT Number (Portal Ref)" fullWidth size="small"
                    value={kotakCreateForm.remittance_number}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, remittance_number: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Kotak Master Ref" fullWidth size="small"
                    value={kotakCreateForm.kotak_reference}
                    InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Remittance Date *" type="date" fullWidth size="small"
                    value={kotakCreateForm.remittance_date}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, remittance_date: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Completion Date" type="date" fullWidth size="small"
                    value={kotakCreateForm.completion_date}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, completion_date: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Beneficiary *" select fullWidth size="small"
                    value={kotakCreateForm.beneficiary_id}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, beneficiary_id: e.target.value })}>
                    <MenuItem value=""><em>— Select beneficiary —</em></MenuItem>
                    {beneficiaries.map(b => <MenuItem key={b.id} value={b.id}>{b.name} ({b.country})</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Currency" select fullWidth size="small"
                    value={kotakCreateForm.currency}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, currency: e.target.value })}>
                    {['USD','EUR','GBP','CNY','AED','SGD'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Amount *" type="number" fullWidth size="small"
                    value={kotakCreateForm.amount}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, amount: e.target.value })} />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Exchange Rate (₹)" type="number" fullWidth size="small"
                    value={kotakCreateForm.exchange_rate}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, exchange_rate: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="INR Principal (₹)" type="number" fullWidth size="small"
                    value={kotakCreateForm.inr_amount}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, inr_amount: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Bank Charges (₹)" type="number" fullWidth size="small"
                    value={kotakCreateForm.bank_charges}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, bank_charges: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="GST on Charges (₹)" type="number" fullWidth size="small"
                    value={kotakCreateForm.bank_charges_gst}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, bank_charges_gst: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="FCC-GST on Charges (₹)" type="number" fullWidth size="small"
                    value={kotakCreateForm.bank_charges_fcc_gst}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, bank_charges_fcc_gst: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Remittance Type / Purpose" fullWidth size="small"
                    value={kotakCreateForm.purpose_description}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, purpose_description: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Notes" fullWidth size="small" multiline rows={2}
                    value={kotakCreateForm.notes}
                    onChange={e => setKotakCreateForm({ ...kotakCreateForm, notes: e.target.value })} />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setKotakPdfResult(null); setKotakCreateForm(null); }}>
            {kotakPdfResult?.action === 'updated' ? 'Close' : 'Cancel'}
          </Button>
          {kotakPdfResult?.action === 'not_found' && (
            <Button variant="contained" disabled={kotakCreateLoading || !kotakCreateForm?.beneficiary_id} onClick={handleKotakCreate}>
              {kotakCreateLoading ? 'Creating...' : 'Create Remittance'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={Boolean(viewDialog)} onClose={() => setViewDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Remittance Details</DialogTitle>
        <DialogContent>
          {viewDialog && (
            <Box sx={{ fontSize: 13 }}>
              {[
                ['Kotak Ref',       viewDialog.kotak_reference],
                ['FT Number',       viewDialog.remittance_number],
                ['Date',            viewDialog.remittance_date],
                ['Completion Date', viewDialog.completion_date],
                ['Beneficiary',     viewDialog.beneficiary_name],
                ['Currency',        viewDialog.currency],
                ['Amount',          `${viewDialog.currency} ${parseFloat(viewDialog.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
                ['Exchange Rate',   viewDialog.exchange_rate ? `₹ ${parseFloat(viewDialog.exchange_rate).toFixed(4)}` : null],
                ['INR Principal',   viewDialog.inr_amount ? `₹ ${parseFloat(viewDialog.inr_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : null],
                ['Bank Charges',    viewDialog.bank_charges ? `₹ ${parseFloat(viewDialog.bank_charges).toFixed(2)}` : null],
                ['GST on Charges',  viewDialog.bank_charges_gst ? `₹ ${parseFloat(viewDialog.bank_charges_gst).toFixed(2)}` : null],
                ['Total Debit',     viewDialog.inr_amount ? `₹ ${(parseFloat(viewDialog.inr_amount||0)+parseFloat(viewDialog.bank_charges||0)+parseFloat(viewDialog.bank_charges_gst||0)).toLocaleString('en-IN',{minimumFractionDigits:2})}` : null],
                ['Bank Status',     viewDialog.status],
                ['Open/Closed',     viewDialog.remittance_status || 'open'],
                ['Purpose',         viewDialog.purpose_description || viewDialog.purpose_code],
                ['Notes',           viewDialog.notes],
              ].filter(([,v]) => v).map(([label, val]) => (
                <Box key={label} sx={{ display: 'flex', gap: 2, py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>{label}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all' }}>{val}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
